import { useCallback, useContext, useState } from "react";
import PreferenceContext from "../context/PreferenceContext";
import useUser from "./useUser";
import PreferencesService from "../services/preferences.service";
import { NotificationSeverities } from "../enums/NotificationSeverities";
import { SavePreferenceConfig } from "../interfaces/SavePreferenceConfig";

const useSavePreference = <TFilters>(
  filters: TFilters,
  config: SavePreferenceConfig<TFilters>
): {
  savePreferenceHandler: () => void;
  isSaveButtonDisabled: boolean;
  isNotificationOpen: boolean;
  notificationMessage: string;
  notificationSeverity: NotificationSeverities;
  notificationCloseHandler: () => void;
  tooltipMessage: string;
} => {
  const { setPreferenceID } = useContext(PreferenceContext);
  const user = useUser();
  const [notificationState, setNotificationState] = useState<{
    isOpen: boolean;
    message: string;
    severity: NotificationSeverities;
  }>({
    isOpen: false,
    message: '',
    severity: NotificationSeverities.INFO,
  });
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState<boolean>(false);

  const savePreferenceHandler = useCallback(() => {
    if (user?.email) {
      const postPreferencePayload = config.buildPayload(filters, user.email);
      setIsSaveButtonDisabled(true);

      PreferencesService.create(postPreferencePayload)
        .then((preferenceID: number) => {
          setPreferenceID(preferenceID);
          setNotificationState({
            isOpen: true,
            message: config.messages.success,
            severity: NotificationSeverities.SUCCESS,
          });
        })
        .catch((error: Error) => {
          setNotificationState({
            isOpen: true,
            message: config.messages.error,
            severity: NotificationSeverities.ERROR,
          });
          console.error(error);
        })
        .finally(() => setIsSaveButtonDisabled(false));
    } else {
      setNotificationState({
        isOpen: true,
        message: 'User not logged in. Cannot save preference.',
        severity: NotificationSeverities.ERROR,
      });
    }
  }, [filters, user, setPreferenceID, config]);

  const notificationCloseHandler = () => {
    setNotificationState((prev) => ({ ...prev, isOpen: false }));
  };

  return {
    savePreferenceHandler,
    isSaveButtonDisabled,
    isNotificationOpen: notificationState.isOpen,
    notificationMessage: notificationState.message,
    notificationSeverity: notificationState.severity,
    notificationCloseHandler,
    tooltipMessage: config.messages.saveTooltip
  }
}

export default useSavePreference;
